/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable array-bracket-newline */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import {
  prepareSendTransaction,
  sendTransaction,
  waitForTransaction,
} from 'wagmi/actions';
import { parseEther } from 'viem';
import Text from '../../../atoms/Text';
import DepositWindow from './DepositWindow';
import { actionAppendTransaction } from '../../../actions/transactions';
import { toIntermediateTransaction } from '../../../helpers/intermediateTransactions';
import {
  GWEI,
  ILimitNativeToken,
  LimitTabs,
} from '../reducers/dex/limitReducer';

/**
 * Implements a gas panel with actual balance and ability to refill
 */
interface IGasPanelProps {
  chainId: number;
  nativeToken: ILimitNativeToken;
  proxyAddress?: string;
  onRefetchBalances(): void;
  onNotify: (input: any) => void;
  onManageProxy(): void;
  limitTab: LimitTabs;
}

enum GasPanelState {
  LOADING,
  ERROR,
  OK,
}

const EMPTY_LEVEL = 0;
const LOW_LEVEL = 500_000 * 5 * GWEI * 2;
const MEDIUM_LEVEL = 500_000 * 5 * GWEI * 5;

const GasPanel = ({
  chainId,
  nativeToken,
  proxyAddress,
  onRefetchBalances,
  onNotify,
  onManageProxy,
  limitTab,
}: IGasPanelProps) => {
  const dispatch = useDispatch();
  const [state, setState] = useState<GasPanelState>(GasPanelState.LOADING);
  const [openDeposit, setOpenDeposit] = useState(false);

  /**
   * Create refill transaction
   */
  const onRefill = async (gasAmount: number) => {
    try {
      setState(GasPanelState.LOADING);
      const refillConfig = await prepareSendTransaction({
        to: proxyAddress,
        value: parseEther(`${gasAmount}`),
      });
      const refillTx = await sendTransaction(refillConfig);
      dispatch(
        actionAppendTransaction(
          toIntermediateTransaction(refillTx.hash, chainId)
        )
      );
      const refillTxTxReceipt = await waitForTransaction(refillTx);
      if (refillTxTxReceipt.status === 'success') {
        setState(GasPanelState.OK);
        onRefetchBalances();
      } else {
        setState(GasPanelState.ERROR);
      }
    } catch (err: any) {
      setState(GasPanelState.ERROR);
      if (err.message.includes('insufficient funds')) {
        onNotify({
          kind: 'error',
          message: `Insufficient funds to deposit 0.1 ${nativeToken.name}`,
        });
      } else {
        onNotify({ kind: 'error', message: err.message });
      }
    }
  };

  useEffect(() => {
    if (nativeToken.loading) {
      setState(GasPanelState.LOADING);
    } else if (nativeToken.error || !proxyAddress) {
      setState(GasPanelState.ERROR);
    } else {
      setState(GasPanelState.OK);
    }
  }, [nativeToken.loading, nativeToken.error, proxyAddress, nativeToken.name]);

  /**
   * Fixes attention tooltip
   */
  useEffect(() => {
    ReactTooltip.rebuild();
  }, [state]);

  const getChargeCondition = () => {
    if (state === GasPanelState.LOADING && limitTab == LimitTabs.Available) {
      return {
        tooltip: 'Loading proxy contract data...',
        charge: 'loading',
      };
    }
    if (state === GasPanelState.OK && limitTab == LimitTabs.Available) {
      if (nativeToken.amount === EMPTY_LEVEL) {
        return {
          tooltip: `Proxy contract doesn't have any<br />${nativeToken.name} balance for gas<br />fees - please top up for trades`,
          charge: 'empty',
        };
      }
      if (nativeToken.amount < LOW_LEVEL) {
        return {
          tooltip: `Proxy contract has ${nativeToken.name}<br />only for ~1-2 trades`,
          charge: 'low',
        };
      }
      if (nativeToken.amount < MEDIUM_LEVEL) {
        return {
          tooltip: `Proxy contract has ${nativeToken.name}<br />for ~3-5 trades`,
          charge: 'medium',
        };
      }
      return {
        tooltip: `Proxy contract has enough ${nativeToken.name}`,
        charge: 'full',
      };
    }
    if (limitTab === LimitTabs.NotAvailable) {
      return {
        tooltip: 'Proxy contract is not deployed...',
        charge: 'empty',
      };
    }
    if (limitTab === LimitTabs.NotSupported) {
      return {
        tooltip: 'Network is not supported yet...',
        charge: 'empty',
      };
    }
    return {
      tooltip: 'Could not fetch proxy contract data...',
      charge: 'error',
    };
  };

  const { charge, tooltip } = getChargeCondition();

  return (
    <div className='gas'>
      <div
        className={`gas__balance gas__balance__fuel--${charge}`}
        onClick={() => setOpenDeposit(true)}
      >
        <div className='gas__balance__fuel icon-fuel' />
        <div className='gas__balance__divider' />
        <div
          className={`gas__balance__charge gas__balance__charge--${charge} icon-charge`}
          data-for='gas-panel-tooltip'
          data-tip={tooltip}
        />
      </div>
      <div
        className={`gas__manage_btn gas__manage_btn--${charge}`}
        onClick={onManageProxy}
      >
        <Text size={12}>Manage Proxy Contract</Text>
        <div className='gas__manage_btn__icon icon-wallet-settings' />
      </div>

      <ReactTooltip
        id='gas-panel-tooltip'
        type='light'
        place='bottom'
        effect='solid'
        className='text_tooltip'
        multiline={true}
      />

      {openDeposit && (
        <DepositWindow
          onClose={() => setOpenDeposit(false)}
          ntCurrency={nativeToken.name}
          onDeposit={onRefill}
        />
      )}
    </div>
  );
};

export default GasPanel;
